import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import ItemList from '../../components/ItemList/ItemList';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';

// Estilos
import styles from './turmaspage.module.css';

// API
import { useEmpresaService } from '../../services/empresaServices';
import { useTurmaService } from '../../services/turmaServices';
import { useTesteService } from '../../services/testeServices';
import { useTrilhaService } from '../../services/trilhaServices';

// Constantes
import { URLS } from '../../constants/urls';

function TurmasPage() {
  const { idEmpresa } = useParams();
  const navigate = useNavigate();

  const { logout } = useAuth();
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();
  const { showModal } = useModal();

  const { obterEmpresaPorId } = useEmpresaService();
  const { listarTurmasPorEmpresa, deletarTurma } = useTurmaService();
  const { obterTestePorId } = useTesteService();
  const { obterTrilhaPorId } = useTrilhaService();

  const [empresa, setEmpresa] = useState(null);
  const [turmas, setTurmas] = useState([]);

  useEffect(() => {
    const carregarDados = async () => {
      showLoader('Carregando turmas');

      const empresaResponse = await obterEmpresaPorId(idEmpresa);

      if (empresaResponse.success) {
        setEmpresa(empresaResponse.data);
      } else {
        showToast(
          'Erro ao carregar dados da empresa: ' + empresaResponse.message,
          3000,
          'error'
        );
        if (empresaResponse.message?.toLowerCase().includes('token')) logout();
        hideLoader();
        return;
      }

      const turmasResponse = await listarTurmasPorEmpresa(idEmpresa);

      if (turmasResponse.success) {
        const turmasComVinculo = await Promise.all(
          (turmasResponse.data || []).map(async (turma) => {
            let nomeVinculo = '';
            let tipoVinculoLabel = '';

            try {
              const temTrilha =
                turma.idTrilha !== null &&
                typeof turma.idTrilha !== 'undefined' &&
                turma.idTrilha !== '';

              const temTeste =
                turma.idTeste !== null &&
                typeof turma.idTeste !== 'undefined' &&
                turma.idTeste !== '';

              if (temTrilha) {
                tipoVinculoLabel = 'Trilha';
                const trilhaResponse = await obterTrilhaPorId(turma.idTrilha);
                if (trilhaResponse.success) {
                  // normalmente trilha vem com "titulo"
                  nomeVinculo = trilhaResponse.data?.titulo || 'Trilha sem título';
                } else {
                  console.warn(
                    `Erro ao carregar trilha da turma ${turma.nome}:`,
                    trilhaResponse.message
                  );
                }
              } else if (temTeste) {
                tipoVinculoLabel = 'Teste';
                const testeResponse = await obterTestePorId(turma.idTeste);
                if (testeResponse.success) {
                  nomeVinculo = testeResponse.data?.nome || 'Teste sem nome';
                } else {
                  console.warn(
                    `Erro ao carregar teste da turma ${turma.nome}:`,
                    testeResponse.message
                  );
                }
              } else {
                tipoVinculoLabel = 'Vínculo';
                nomeVinculo = '';
              }
            } catch (error) {
              console.error(
                `Erro inesperado ao buscar vínculo da turma ${turma.nome}:`,
                error
              );
            }

            const sufixo =
              nomeVinculo
                ? `${tipoVinculoLabel}: ${nomeVinculo}`
                : `${tipoVinculoLabel} desconhecido`;

            return {
              id: turma.id,
              name: `${turma.nome} - ${sufixo}`,
              status: turma.status,
            };
          })
        );

        setTurmas(turmasComVinculo);
      } else {
        showToast('Erro ao carregar turmas.', 3000, 'error');
        if (turmasResponse.message?.toLowerCase().includes('token')) logout();
      }

      hideLoader();
    };

    if (idEmpresa) carregarDados();
  }, [idEmpresa]);

  const breadcrumbIds = empresa
    ? [{ id: idEmpresa, label: 'Turmas da ' + empresa.nome }]
    : [];

  const handleEditar = (item) => {
    navigate(`/empresas/${idEmpresa}/atualizar/${item.id}`);
  };

  const handleExcluir = (item) => {
    showModal(
      `Excluir ${item.name}?`,
      'Essa ação não poderá ser desfeita.',
      async () => {
        showLoader('Excluindo turma');

        const response = await deletarTurma(item.id);

        if (response.success) {
          showToast('Turma excluída com sucesso!', 3000, 'success');
          setTurmas((prev) => prev.filter((e) => e.id !== item.id));
        } else {
          showToast('Falha ao excluir turma.', 3000, 'error');
          console.log(response.message);

          if (response.message?.toLowerCase().includes('token')) logout();
        }

        hideLoader();
      },
      () => {
        showToast('Exclusão cancelada.', 2000);
      }
    );
  };

  const handleSelecionar = (item) => {
    navigate(`/empresas/${idEmpresa}/${item.id}`);
  };

  const handleAdd = async () => {
    navigate(`/empresas/${idEmpresa}/adicionarTurma`);
  };

  return (
    <div className={styles.TurmasPage}>
      <div className={styles.containerTop}>
        <Breadcrumbs idLabels={breadcrumbIds} />

        <div className={styles.logoContentWrapper}>
          <img
            src={
              empresa?.logo
                ? `${URLS.IMG_PATH}${empresa.logo}`
                : 'https://via.placeholder.com/120'
            }
            alt={`Logo da ${empresa?.nome || 'empresa'}`}
            className={styles.logoEmpresa}
          />

          <div className={styles.textos}>
            <h1 className={styles.title}>
              Turmas da {empresa?.nome || 'empresa'}
            </h1>

            <FriendlyPhrase page={'turmas'} />
          </div>
        </div>
      </div>

      <div className={styles.containerList}>
        <ItemList
          items={turmas}
          onAdd={handleAdd}
          onEdit={handleEditar}
          onDelete={handleExcluir}
          onClickItem={handleSelecionar}
        />
      </div>
    </div>
  );
}

export default TurmasPage;